// OpenAI API Client
// https://platform.openai.com/docs/api-reference

import type { HttpRequest } from '../http.js'
import { createRequestAPI } from './utils.js'

export interface OpenaiChatCompletionsArgs {
  apiKey?: string
  model?: string
  messages: unknown[]
  temperature?: number
  max_tokens?: number
  top_p?: number
  frequency_penalty?: number
  presence_penalty?: number
  stream?: boolean
  stop?: string | string[]
  n?: number
  user?: string
  tools?: unknown[]
  tool_choice?: unknown
}

export interface OpenaiClient {
  chatCompletions(args: OpenaiChatCompletionsArgs): Promise<any>
  listModels(args: { apiKey?: string }): Promise<any>
  retrieveModel(args: { apiKey?: string; model: string }): Promise<any>
}

export function createOpenaiClient(httpRequest: HttpRequest): OpenaiClient {
  const requestAPI = createRequestAPI(httpRequest)

  const chatCompletions = async ({
    apiKey,
    model = 'gpt-4',
    messages,
    temperature,
    max_tokens,
    top_p,
    frequency_penalty,
    presence_penalty,
    stream = false,
    stop,
    n,
    user,
    tools,
    tool_choice,
  }: OpenaiChatCompletionsArgs) => {
    if (!messages || !Array.isArray(messages) || messages.length === 0) {
      throw new Error('messages is required and must be a non-empty array')
    }

    const bodyParams = {
      model,
      messages,
      temperature,
      max_tokens,
      top_p,
      frequency_penalty,
      presence_penalty,
      stream,
      stop,
      n,
      user,
      tools,
      tool_choice,
    }

    const body = Object.fromEntries(
      Object.entries(bodyParams).filter(([_, value]) => value !== undefined),
    )

    return await requestAPI({
      path: '/v1/chat/completions',
      method: 'POST',
      apiKey,
      body,
    })
  }

  const listModels = async ({ apiKey }: { apiKey: string }) => {
    return await requestAPI({ path: '/v1/models', method: 'GET', apiKey, body: {} })
  }

  const retrieveModel = async ({ apiKey, model }: { apiKey: string; model: string }) => {
    if (!model) throw new Error('model is required')
    return await requestAPI({
      path: `/v1/models/${model}`,
      method: 'GET',
      apiKey,
      body: {},
    })
  }

  return { chatCompletions, listModels, retrieveModel }
}
